import { useEffect, useMemo, useRef, useState } from 'react';

import {
  Button,
  Checkbox,
  Input,
  InputNumber,
  Radio,
  Space,
  Tooltip,
  Typography,
  Select,
} from 'antd';
import { ApartmentOutlined, SearchOutlined } from '@ant-design/icons';

import { CustomNotification } from 'common/components';

import { ProductEditBrandModal } from 'pages/product-full-view/components';
import StatusCellRenderer from '../components/StatusCellRenderer';
import WizardTextCellEditor from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/components/WizardTextCellEditor';
import {
  LENGTH_PRODUCT_NAME,
  LENGTH_PRODUCT_DESCRIPTION,
} from 'common/components/product-add/components/constants';
import { useGetBasicUomEnum } from 'hooks';
import useSaveBrandWizardProduct from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/useSaveBrandWizardProduct';

import { deriveAnswersFromResponse } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/wizardProduct';

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

export const HeaderGrid = ({ tooltip, label, required = false }) => {
  return (
    <Tooltip title={tooltip}>
      <span>
        {required && (
          <Typography.Text type='danger' style={{ marginRight: 4 }}>
            *
          </Typography.Text>
        )}
        <Typography.Text strong style={{ fontSize: 14 }}>
          {label}
        </Typography.Text>
      </span>
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// Cell renderers (read-only display)
// ---------------------------------------------------------------------------

const YesNoCellRenderer = ({ value }) => {
  const isYes = value === 'Y' || value === true || value === 'Yes';
  return (
    <Radio.Group value={isYes}>
      <Radio value={true}>Yes</Radio>
      <Radio value={false}>No</Radio>
    </Radio.Group>
  );
};

// ---------------------------------------------------------------------------
// NoCodeCase checkbox cell renderer
//
// Renders a checkbox + label inline. Committing via node.setDataValue so it
// fires onCellValueChanged exactly like BrandCellRenderer does.
// When checked → also clears caseGtin on the same row so the two fields
// stay consistent without requiring a separate save action.
// ---------------------------------------------------------------------------

const NoCodeCaseCellRenderer = (props) => {
  const checked = !!props.value;

  const handleChange = (e) => {
    const next = e.target.checked;
    props.context?.onCellValueChanged?.({
      colDef: { field: 'noCodeCase' },
      newValue: next,
      data: props.node.data,
      node: props.node,
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        paddingLeft: 4,
      }}
    >
      <Checkbox checked={checked} disabled={isVariableWeight(props)} onChange={handleChange} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Cell editors (inline editing)
// ---------------------------------------------------------------------------

export const YesNoCellEditor = (props) => {
  const { initialValue, onValueChange } = props;
  const [value, setValue] = useState(initialValue ?? false);
  const valueRef = useRef(initialValue ?? false);

  const updateValue = (next) => {
    valueRef.current = next;
    setValue(next);
    onValueChange(next);
  };

  // An empty cell shows "No" selected; commit that if the user just confirms
  useEffect(() => {
    if (initialValue == null) onValueChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    updateValue(e.target.value === true || e.target.value === 'true');
    props.stopEditing();
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.stopPropagation();
        e.preventDefault();
        updateValue(!valueRef.current);
      }

      if (e.key === 'Enter') {
        e.stopPropagation();
        e.preventDefault();
        props.stopEditing();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ padding: '6px 10px', background: '#fff' }}>
      <Radio.Group value={value} onChange={handleChange}>
        <Radio value={true}>Yes</Radio>
        <Radio value={false}>No</Radio>
      </Radio.Group>
    </div>
  );
};
YesNoCellEditor.displayName = 'YesNoCellEditor';

export const NumberCellEditor = (props) => {
  const [value, setValue] = useState(props.initialValue ?? null);

  const handleChange = (next) => {
    setValue(next);
    props.onValueChange(next);
  };

  return (
    <InputNumber
      autoFocus
      value={value}
      onChange={handleChange}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
          props.stopEditing();
          return;
        }
        if (
          !/^\d$/.test(e.key) &&
          !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(e.key)
        ) {
          e.preventDefault();
        }
      }}
      onBlur={() => props.stopEditing()}
      style={{ width: '100%', height: '100%' }}
      min={0}
    />
  );
};
NumberCellEditor.displayName = 'NumberCellEditor';

const toDecimalValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = parseFloat(value);
  return isNaN(num) ? null : Math.round(num * 1000) / 1000;
};

export const DecimalCellEditor = (props) => {
  const [value, setValue] = useState(props.initialValue ?? null);

  const handleChange = (next) => {
    setValue(next);
    props.onValueChange(toDecimalValue(next));
  };

  return (
    <InputNumber
      autoFocus
      value={value}
      onChange={handleChange}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
          props.stopEditing();
          return;
        }
        if (
          !/^\d$/.test(e.key) &&
          e.key !== '.' &&
          !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(e.key)
        ) {
          e.preventDefault();
        }
      }}
      onBlur={() => props.stopEditing()}
      style={{ width: '100%', height: '100%' }}
      min={0}
      step={0.001}
      precision={3}
    />
  );
};
DecimalCellEditor.displayName = 'DecimalCellEditor';

export const UpcCellEditor = (props) => {
  const [value, setValue] = useState(props.initialValue ?? '');

  const handleChange = (e) => {
    // Strip non-digits and cap at 13 characters
    const next = e.target.value.replace(/\D/g, '').slice(0, 13);
    setValue(next);
    props.onValueChange(next);
  };

  return (
    <Input
      autoFocus
      inputMode='numeric'
      value={value}
      onChange={handleChange}
      onBlur={() => props.stopEditing()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === 'Tab') props.stopEditing();
      }}
      maxLength={13}
      style={{ width: '100%', height: '100%' }}
      bordered={false}
    />
  );
};
UpcCellEditor.displayName = 'UpcCellEditor';

export const CaseUpcCellEditor = (props) => {
  const [value, setValue] = useState(props.initialValue ?? '');

  const handleChange = (e) => {
    // Strip non-digits and cap at 12 characters
    const next = e.target.value.replace(/\D/g, '').slice(0, 12);
    setValue(next);
    props.onValueChange(next);
  };

  return (
    <Input
      autoFocus
      inputMode='numeric'
      value={value}
      onChange={handleChange}
      onBlur={() => props.stopEditing()}
      // Enter and Tab are both handled by this column's suppressKeyboardEvent
      // (see colDef below), which owns stopEditing()/navigation for those keys.
      // Handling them here too would fire onCellValueChanged twice per edit.
      style={{ width: '100%', height: '100%' }}
    />
  );
};
CaseUpcCellEditor.displayName = 'CaseUpcCellEditor';

export const GtinCellEditor = (props) => {
  const [value, setValue] = useState(props.initialValue ?? '');

  const handleChange = (e) => {
    // Strip non-digits and cap at 14 characters
    const next = e.target.value.replace(/\D/g, '').slice(0, 14);
    setValue(next);
    props.onValueChange(next);
  };

  return (
    <Input
      autoFocus
      inputMode='numeric'
      value={value}
      onChange={handleChange}
      onBlur={() => props.stopEditing()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === 'Tab') props.stopEditing();
      }}
      maxLength={14}
      style={{ width: '100%', height: '100%' }}
      bordered={false}
    />
  );
};
GtinCellEditor.displayName = 'GtinCellEditor';

export const SizeUomCellEditor = (props) => {
  const [value, setValue] = useState(props.initialValue ?? null);

  const { basicUomProperties } = useGetBasicUomEnum();

  const enumOptions =
    basicUomProperties?.filter((type) =>
      ['Mass', 'Count', 'Volume', 'Dimensions', 'Area'].includes(type.enumGroup)
    ) ?? [];

  const handleSelect = (selected) => {
    setValue(selected);
    props.onValueChange(selected);
    setTimeout(() => props.stopEditing(), 0);
  };

  return (
    <Select
      autoFocus
      showSearch
      value={value}
      onSelect={handleSelect}
      onChange={handleSelect}
      style={{ width: '180px' }}
      filterOption={(input, option) =>
        option?.children?.toLowerCase().includes(input.toLowerCase())
      }
      getPopupContainer={(trigger) => trigger.parentElement}
    >
      {enumOptions
        .slice()
        .sort((a, b) => a.enumDisplayName.localeCompare(b.enumDisplayName))
        .map((type, index) => (
          <Select.Option value={type.enumDisplayName} key={index}>
            {type.enumDisplayName}
          </Select.Option>
        ))}
    </Select>
  );
};
SizeUomCellEditor.displayName = 'SizeUomCellEditor';

export const BrandCellRenderer = (props) => {
  const [modalVisible, setModalVisible] = useState(false);

  const { saveBrand } = useSaveBrandWizardProduct();

  const brandName = props.value;
  const memberId = props.memberId;

  useEffect(() => {
    props.node.__openBrandModal = () => setModalVisible(true);
    return () => {
      delete props.node.__openBrandModal;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpen = (e) => {
    e.stopPropagation();
    setModalVisible(true);
  };

  const handleSelectBrand = async (brand) => {
    if (!brand?.brandName) return;

    let brandId = brand.id;

    if (brand.file) {
      const res = await saveBrand({
        id: brand.id,
        brandName: brand.brandName,
        memberId: brand.memberId ?? memberId,
        file: brand.file,
      });

      if (!res?.isSuccess) {
        CustomNotification.error(res?.message ?? 'Failed to save brand');
        throw new Error(res?.message);
      }

      brandId = res.data?.brandId ?? brand.id;
    }

    props.node.setDataValue(props.column.getColId(), brand.brandName);
    props.node.data.brandId = brandId;

    props.api.refreshCells({
      rowNodes: [props.node],
      columns: ['brandId'],
      force: true,
    });

    props.context?.onCellValueChanged?.({
      colDef: { field: 'brandName' },
      newValue: brand.brandName,
      data: props.node.data,
      node: props.node,
    });

    props.context?.onCellValueChanged?.({
      colDef: { field: 'brandId' },
      newValue: brandId,
      data: props.node.data,
      node: props.node,
    });

    setTimeout(() => {
      props.api.setFocusedCell(props.node.rowIndex, props.column.getColId());
    }, 0);
  };

  const handleCancel = () => {
    setModalVisible(false);
    // Restore focus on cancel too
    setTimeout(() => {
      props.api.setFocusedCell(props.node.rowIndex, props.column.getColId());
    }, 0);
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <div
        onClick={handleOpen}
        style={{
          cursor: 'pointer',
          flex: 1,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {brandName || <Button type='text'>Select Brand</Button>}
        </span>
      </div>

      {modalVisible && (
        <ProductEditBrandModal
          visible={modalVisible}
          setVisibleEditBrand={handleCancel}
          productDetail={{}}
          isAddProduct
          isEdit={false}
          skipFormSubmit={true}
          isWizard
          onSelectBrand={handleSelectBrand}
          setBrandId={() => {}}
          setBrandThumb={() => {}}
          memberIdFromBrand={memberId}
          zIndex={1200}
        />
      )}
    </div>
  );
};

BrandCellRenderer.displayName = 'BrandCellRenderer';

const BrandCellEditor = (props) => {
  useEffect(() => {
    props.stopEditing();
    setTimeout(() => {
      props.node.__openBrandModal?.();
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};
BrandCellEditor.displayName = 'BrandCellEditor';

// ---------------------------------------------------------------------------
// Recapture action cell
// ---------------------------------------------------------------------------

const RecaptureCellRenderer = ({ data, onRecaptureRow }) => {
  return (
    <Tooltip title='Search existing product'>
      <Button
        type='primary'
        icon={<SearchOutlined style={{ fontSize: 14 }} />}
        onClick={(e) => {
          e.stopPropagation();
          onRecaptureRow?.(data);
        }}
        style={{ width: 28, height: 28, padding: 0, borderRadius: 4 }}
      />
    </Tooltip>
  );
}; // ---------------------------------------------------------------------------
// Error cell renderer
//
// Previously used `find` — only showed the first error per field.
// Now uses `filter` to collect ALL errors for the field and joins them
// into a single tooltip so multiple BE validation messages are all visible.
// null-field errors (product-level warnings like "captured within 90 days")
// are skipped here — they're shown via the status cell's validation popover instead.
// ---------------------------------------------------------------------------

export const ErrorCellRenderer = (props) => {
  const data = props.node.data;
  const { value, colDef, innerRenderer: InnerRenderer } = props;

  const fieldErrors = (data?.validationErrors ?? []).filter(
    (err) =>
      err.messageType === 'Error' &&
      err.field !== null &&
      err.field?.toLowerCase() === colDef?.field?.toLowerCase()
  );
  const content = InnerRenderer ? (
    <InnerRenderer {...props} />
  ) : (
    <span>{value}</span>
  );

  if (fieldErrors.length === 0) return content;

  // Join multiple error messages with a bullet separator
  const tooltipTitle =
    fieldErrors.length === 1
      ? fieldErrors[0].message
      : fieldErrors.map((err, i) => <div key={i}>{`• ${err.message}`}</div>);

  return (
    <Tooltip
      title={tooltipTitle}
      color='#ff4d4f'
      zIndex={1100}
      placement='top'
      overlayStyle={{ maxWidth: 320 }}
    >
      <Typography.Text
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          minHeight: 20,
        }}
      >
        {content}
      </Typography.Text>
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const isNoCodeCase = (params) => !!params.data?.noCodeCase;

const isDisplayShipper = (params) => params.data?.displayShipper === true;

const DISABLED_STYLE = {
  backgroundColor: '#f5f5f5',
  color: '#bfbfbf',
  cursor: 'not-allowed',
};

// AG Grid ignores a null cellStyle and keeps the cell's previous style; an empty object clears it
const NO_CELL_STYLE = {};

const disabledCellStyle = (params) =>
  isDisplayShipper(params) ? DISABLED_STYLE : NO_CELL_STYLE;

const extendDisabledCellStyle =
  (...extraConditions) =>
  (params) => {
    if (isDisplayShipper(params)) return DISABLED_STYLE;
    if (extraConditions.some((condition) => condition(params)))
      return DISABLED_STYLE;
    return NO_CELL_STYLE;
  };

const isFoodService = (params) =>
  params.node.data?.foodServiceBulkItems === true;

const isVariableWeight = (params) => params.node.data?.variableWeight === true;

const hasNoInnerPack = (params) =>
  isFoodService(params) || isVariableWeight(params);

const isInnerPackSellable = (params) =>
  params.node.data?.innerPackSellable === true;

const caseGtinCellStyle = (params) => {
  if (isNoCodeCase(params)) {
    return {
      backgroundColor: '#f5f5f5',
      color: '#bfbfbf',
      cursor: 'not-allowed',
    };
  }
  const hasError = params.data?.validationErrors?.some(
    (err) => err.messageType === 'Error' && err.field === 'caseGtin'
  );
  if (hasError) {
    return { backgroundColor: '#fff1f0', borderLeft: '2px solid #ff4d4f' };
  }
  return null;
};

export const createCellErrorStyle =
  (fieldName, baseStyle = null) =>
  (params) => {
    const base = baseStyle ? baseStyle(params) : null;

    const hasError = params.data?.validationErrors?.some(
      (err) => err.messageType === 'Error' && err.field === fieldName
    );

    if (!hasError) return base ?? NO_CELL_STYLE;

    return {
      ...base,
      backgroundColor: '#fff1f0',
      borderLeft: '2px solid #ff4d4f',
    };
  };

const caseUpc12CellStyle = (params) => {
  if (isNoCodeCase(params)) {
    return DISABLED_STYLE;
  }
  return null;
};

export const useOnlineCoreGridColumns = (
  memberId,
  onRecaptureRow,
  answers,
  onOpenHierarchy
) => {
  const {
    shelfDisplayBoxTray: showMerchandisedInTray,
    perishable: showPerishable,
    foodServiceBulkItems: showFoodServiceBulkItems,
    variableWeight: showVariableWeight,
    displayShippers: showDisplayShippers,
    multipacks: showMultipacks,
  } = deriveAnswersFromResponse(answers);

  const columnDefs = useMemo(
    () => [
      {
        field: '',
        checkboxSelection: true,
        filter: false,
        suppressHeaderMenuButton: true,
        width: 50,
        resizable: true,
        pinned: 'left',
      },
      {
        field: 'status',
        headerName: '',
        width: 50,
        cellRenderer: StatusCellRenderer,
        cellStyle: { padding: 0 },
        pinned: 'left',
        editable: false,
        filter: false,
        suppressHeaderMenuButton: true,
        equals: () => false,
      },
      {
        field: 'recapture',
        headerName: '',
        width: 36,
        pinned: 'left',
        editable: false,
        sortable: false,
        resizable: false,
        cellRenderer: RecaptureCellRenderer,
        cellRendererParams: { onRecaptureRow },
        cellStyle: { padding: 0 },
      },
      {
        field: 'hierarchyId',
        headerComponent: HeaderGrid,
        headerComponentParams: {
          label: 'Hierarchy',
          required: false,
          tooltip: (
            <Typography.Text style={{ color: '#fff' }}>
              A product hierarchy defines how items are packaged and shipped,
              generally structured as{' '}
              <Typography.Text strong style={{ color: '#fff' }}>
                Unit → Inner Pack → Case (Master Pack) → Pallet
              </Typography.Text>
              <br />
              <Typography.Text style={{ color: '#fff', marginTop: 16 }}>
                Click the <strong>Hierarchy</strong> button to select the
                preexisting hierarchy you are sending in for recapture.
              </Typography.Text>
            </Typography.Text>
          ),
        },
        maxWidth: 120,
        editable: false,
        cellRenderer: (params) => {
          const productId =
            params?.data?.productUnitId ||
            params?.data?.productCaseId ||
            params?.data?.productInnerPackId ||
            params?.data?.productId;
          const productHierarchyId = params?.data?.hierarchyId;

          const hasHierarchy = !!productHierarchyId;

          if (!productId) return null;

          return (
            <Space size={18}>
              <Typography.Text strong>
                {hasHierarchy ? 'Yes' : 'No'}
              </Typography.Text>
              <Tooltip title='Change Hierarchy'>
                <Button
                  type='primary'
                  style={{ border: 'none', borderRadius: 4 }}
                  icon={<ApartmentOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenHierarchy?.(params.data);
                  }}
                />
              </Tooltip>
            </Space>
          );
        },
        cellStyle: (params) => {
          const productId =
            params?.data?.productUnitId ||
            params?.data?.productCaseId ||
            params?.data?.productInnerPackId ||
            null;
          return productId
            ? NO_CELL_STYLE
            : {
                backgroundColor: '#f5f5f5',
                color: '#bfbfbf',
                cursor: 'not-allowed',
              };
        },
      },
      ...(showDisplayShippers
        ? [
            {
              field: 'displayShipper',
              headerComponent: HeaderGrid,
              headerComponentParams: {
                label: 'Store Display / Shipper',
                tooltip:
                  'Is this product sold as a display shipper unit? Selecting Yes will disable innerpack and case fields',
              },
              width: 210,
              editable: true,
              singleClickEdit: true,
              cellRenderer: YesNoCellRenderer,
              cellEditor: YesNoCellEditor,
              cellEditorPopup: true,
            },
          ]
        : []),
      {
        field: 'unitUpc',
        headerComponent: HeaderGrid,
        headerComponentParams: {
          label: 'Unit UPC12',
          required: true,
          tooltip:
            '12 Digit UPC on the retail UNIT and/or the lowest package level with a unique UPC - DO NOT USE PUNCTUATION - NUMERIC ONLY',
        },

        width: 140,
        editable: (params) => !isDisplayShipper(params) && !isVariableWeight(params),
        cellStyle: createCellErrorStyle('unitUpc', (params) =>
          (isDisplayShipper(params) || isVariableWeight(params)) ? DISABLED_STYLE : null
        ),
        singleClickEdit: true,
        cellEditor: UpcCellEditor,
        cellRenderer: ErrorCellRenderer,
      },

      ...(showMultipacks
        ? [
            {
              field: 'multipack',
              headerComponent: HeaderGrid,
              headerComponentParams: {
                label: 'Multipack',
                tooltip:
                  'Is this product sold as a multipack? Selecting Yes will enable multipack-specific fields',
              },
              width: 210,
              editable: true,
              singleClickEdit: true,
              cellRenderer: YesNoCellRenderer,
              cellEditor: YesNoCellEditor,
              cellEditorPopup: true,
            },
          ]
        : []),
      {
        field: 'vendorItemNumber',
        headerComponent: HeaderGrid,
        headerComponentParams: {
          label: 'Vendor Item # / SKU',
          tooltip:
            'Vendor Item # is the supplier’s internal SKU or catalog number used to identify and manage the product.',
        },
        width: 226,
        editable: true,
        singleClickEdit: true,
      },
      {
        field: 'brandId',
        width: 0,
        minWidth: 0,
        maxWidth: 0,
        hide: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true,
      },
      {
        field: 'brandName',
        headerComponent: HeaderGrid,
        headerComponentParams: {
          label: 'Brand Name',
          required: true,
          tooltip:
            'Item BRAND NAME - This should be consistent throughout all of your products',
        },
        width: 160,
        editable: true,
        singleClickEdit: true,
        cellEditor: BrandCellEditor, // ← opens modal then immediately stops editing
        cellRenderer: ErrorCellRenderer, // ← shows errors with tooltip but otherwise just displays the brand name
        cellRendererParams: {
          memberId,
          innerRenderer: BrandCellRenderer,
        },
        cellStyle: createCellErrorStyle('brandName'),
        suppressKeyboardEvent: (params) => {
          if (params.event.key === 'Enter') {
            // Open brand modal directly via node ref instead of entering edit mode.
            // suppressKeyboardEvent returns true to prevent AG Grid from processing
            // Enter (which would trigger singleClickEdit and mount BrandCellEditor).
            params.node.__openBrandModal?.();
            return true;
          }
          return false;
        },
      },
      {
        field: 'productName',
        headerComponent: HeaderGrid,
        headerComponentParams: {
          label: 'Product Name',
          required: true,
          tooltip:
            'Main item name: This is a brief description of your product',
        },
        minWidth: 200,
        flex: 1,
        editable: true,
        singleClickEdit: true,
        cellStyle: createCellErrorStyle('productName'),
        cellRenderer: ErrorCellRenderer,
        cellEditor: WizardTextCellEditor,
        cellEditorParams: { maxLength: LENGTH_PRODUCT_NAME },
        cellEditorPopup: true,
      },
      {
        field: 'productDescription',
        headerComponent: HeaderGrid,
        headerComponentParams: {
          label: 'Product Description',
          required: true,
          tooltip:
            'Main item description: This is a consumer-friendly brief description of your product designed to help differentiate your product',
        },
        width: 220,
        editable: true,
        singleClickEdit: true,
        cellStyle: createCellErrorStyle('productDescription'),
        cellRenderer: ErrorCellRenderer,
        cellEditor: WizardTextCellEditor,
        cellEditorParams: { maxLength: LENGTH_PRODUCT_DESCRIPTION },
        cellEditorPopup: true,
      },
      {
        field: 'ecommerceDescription',
        headerComponent: HeaderGrid,
        headerComponentParams: {
          label: 'eCom Description',
          required: false,
          tooltip:
            'Main item description:  Usually longer and more robust than the Product Description.  This is exactly how you recommend the product’s name be displayed on a retailer’s website.',
        },
        width: 220,
        editable: true,
        singleClickEdit: true,
      },
      {
        field: 'size',
        cellDataType: 'number',
        headerComponent: HeaderGrid,
        headerComponentParams: {
          label: 'Primary Size',
          required: true,
          tooltip: 'Numerical value printed on the item indicating the size',
        },
        width: 140,
        editable: true,
        singleClickEdit: true,
        cellEditor: DecimalCellEditor,
        cellStyle: createCellErrorStyle('size'),
        cellRenderer: ErrorCellRenderer,
      },
      {
        field: 'sizeUom',
        headerComponent: HeaderGrid,
        headerComponentParams: {
          label: 'Primary Size UoM',
          required: true,
          tooltip:
            'Primary Unit of Measure on the label (use dropdown for industry standards)',
        },
        width: 180,
        editable: true,
        singleClickEdit: true,
        cellEditor: SizeUomCellEditor,
        cellEditorPopup: true,
        cellStyle: createCellErrorStyle('sizeUom'),
        cellRenderer: ErrorCellRenderer,
        suppressKeyboardEvent: (params) => params.event.key === 'Enter',
      },

      ...(showMerchandisedInTray
        ? [
            {
              field: 'merchandisedInTray',
              headerComponent: HeaderGrid,
              headerComponentParams: {
                label: 'Merchandised In Tray?',
                tooltip:
                  'Is the unit contained in a shelf display box/tray? True for Yes. False for No.',
              },
              width: 200,
              editable: (params) => !isDisplayShipper(params),
              cellStyle: (params) =>
                isDisplayShipper(params) ? DISABLED_STYLE : NO_CELL_STYLE,
              singleClickEdit: true,
              cellRenderer: YesNoCellRenderer,
              cellEditor: YesNoCellEditor,
              cellEditorPopup: true,
            },
          ]
        : []),

      ...(showPerishable
        ? [
            {
              field: 'perishable',
              headerComponent: HeaderGrid,
              headerComponentParams: {
                label: 'Perishable',
                tooltip:
                  'Is the item refrigerated or frozen? True for Yes. False for No.',
              },
              width: 160,
              editable: true,
              singleClickEdit: true,
              cellRenderer: YesNoCellRenderer,
              cellEditor: YesNoCellEditor,
              cellEditorPopup: true,
            },
          ]
        : []),

      ...(showFoodServiceBulkItems
        ? [
            {
              field: 'foodServiceBulkItems',
              headerComponent: HeaderGrid,
              headerComponentParams: {
                label: 'Foodservice or Bulk Pack',
                tooltip:
                  'Is this product Foodservice and/or Bulk? True for Yes. False for No.',
              },
              width: 210,
              editable: true,
              singleClickEdit: true,
              cellRenderer: YesNoCellRenderer,
              cellEditor: YesNoCellEditor,
              cellEditorPopup: true,
            },
          ]
        : []),

      ...(showVariableWeight
        ? [
            {
              field: 'variableWeight',
              headerComponent: HeaderGrid,
              headerComponentParams: {
                label: 'Variable Weight / Catch Weight',
                tooltip:
                  'Is this product a Variable Weight or Catch Weight item? True for Yes. False for No.',
              },
              width: 250,
              editable: true,
              singleClickEdit: true,
              cellRenderer: YesNoCellRenderer,
              cellEditor: YesNoCellEditor,
              cellEditorPopup: true,
            },
          ]
        : []),

      {
        field: 'innerPackUpc',
        headerComponent: HeaderGrid,
        headerComponentParams: {
          label: 'Innerpack UPC',
          tooltip:
            '(If applicable) 12 digit UPC on the INNERPACK; CANNOT be the same UPC as the Unit or the Case; REQUIRED if Innerpack is sellable',
        },
        width: 160,
        editable: (params) =>
          !isDisplayShipper(params) && !hasNoInnerPack(params),
        singleClickEdit: true,
        cellStyle: createCellErrorStyle(
          'innerPackUpc',
          extendDisabledCellStyle(hasNoInnerPack)
        ),
        cellRenderer: ErrorCellRenderer,
        cellEditor: CaseUpcCellEditor,
      },
      {
        field: 'innerPackGtin',
        headerComponent: HeaderGrid,
        headerComponentParams: {
          label: 'Innerpack GTIN',
          tooltip:
            '(If applicable) The Global Trade Item Number (GTIN) on the INNERPACK CANNOT be the same as the GTIN for the Unit or Case.',
        },
        width: 160,
        editable: (params) =>
          !isDisplayShipper(params) &&
          !hasNoInnerPack(params) &&
          !isInnerPackSellable(params),
        cellStyle: createCellErrorStyle(
          'innerPackGtin',
          extendDisabledCellStyle(hasNoInnerPack, isInnerPackSellable)
        ),
        singleClickEdit: true,
        cellEditor: GtinCellEditor,
        cellRenderer: ErrorCellRenderer,
      },

      {
        field: 'innerPackSellable',
        headerComponent: HeaderGrid,
        headerComponentParams: {
          label: 'Innerpack Retailer Sellable?',
          tooltip:
            'Select Yes when the innerpack can be purchased by the consumer at a retail store and it has its own retail-scannable barcode.',
        },
        width: 230,
        editable: (params) => !isDisplayShipper(params) && !isVariableWeight(params),
        cellStyle: extendDisabledCellStyle(isVariableWeight),
        singleClickEdit: true,
        cellRenderer: YesNoCellRenderer,
        cellEditor: YesNoCellEditor,
        cellEditorPopup: true,
      },

      {
        field: 'innerPackPackSize',
        cellDataType: 'number',
        headerComponent: HeaderGrid,
        headerComponentParams: {
          label: 'Innerpack Pack Size',
          tooltip: '(If applicable) Number of units in the INNERPACK',
        },
        width: 170,
        editable: (params) =>
          !isDisplayShipper(params) && !hasNoInnerPack(params),
        cellStyle: extendDisabledCellStyle(hasNoInnerPack),
        singleClickEdit: true,
        cellEditor: NumberCellEditor,
      },

      // ── No Code Case ─────────────────────────────────────────────────────────
      {
        field: 'noCodeCase',
        headerComponent: HeaderGrid,
        headerComponentParams: {
          label: 'No Case Code',
          tooltip:
            'Select this when the case does not have its own unique barcode, or when no case level exists. When the No Case Code selected, Case UPC-12, Case GTIN, and Is Case Sellable fields are disabled. Not available for Variable Weight / Catch Weight items, which require a Case GTIN or Case UPC-12.',
        },
        width: 226,
        editable: false,
        cellRenderer: NoCodeCaseCellRenderer,
        // Hide for Display Shipper rows
        cellStyle: (params) => {
          if (isDisplayShipper(params)) return { display: 'none' };
          return isVariableWeight(params) ? DISABLED_STYLE : NO_CELL_STYLE;
        },
        colSpan: (params) => (isDisplayShipper(params) ? 0 : 1),
        // Fix #12559 Issue 1: skip in Tab navigation when hidden
        suppressNavigable: (params) => isDisplayShipper(params),
        // Fix #12559 Issue 2: Space toggles the checkbox instead of selecting the row
        suppressKeyboardEvent: (params) => {
          if (params.event.key === ' ' && !isDisplayShipper(params) && !isVariableWeight(params)) {
            params.event.preventDefault();
            params.event.stopPropagation();
            const current = !!params.node.data?.noCodeCase;
            params.context?.onCellValueChanged?.({
              colDef: { field: 'noCodeCase' },
              newValue: !current,
              data: params.node.data,
              node: params.node,
            });
            return true;
          }
          return false;
        },
      },

      // ── Case UPC-12 ──────────────────────────────────────────────────────
      {
        field: 'caseUpc12',
        headerComponent: HeaderGrid,
        headerComponentParams: {
          label: 'Case UPC-12',
          tooltip: '12-digit UPC for the case level.',
        },
        width: 140,
        editable: (params) => !isNoCodeCase(params) || isDisplayShipper(params),
        singleClickEdit: true,
        cellStyle: (params) => {
          if (isDisplayShipper(params)) {
            return createCellErrorStyle('caseUpc12')(params);
          }
          return createCellErrorStyle('caseUpc12', caseUpc12CellStyle)(params);
        },
        cellRenderer: ErrorCellRenderer,
        cellEditor: CaseUpcCellEditor,
        // AG Grid's default Tab handling advances to (and starts editing) the
        // next cell synchronously, BEFORE this cell's onCellValueChanged fires
        // — so the caseGtin editor mounts with a stale value even though our
        // handler auto-fills caseGtin synchronously. Take over both Enter and
        // Tab entirely (rather than mixing manual + default handling) so
        // editing always finishes here first, before AG Grid does anything else.
        suppressKeyboardEvent: (params) => {
          if (!params.editing) return false;
          if (params.event.key === 'Enter') {
            params.event.preventDefault();
            params.api.stopEditing();
            return true;
          }
          if (params.event.key === 'Tab') {
            params.event.preventDefault();
            params.api.stopEditing();
            // setFocusedCell (not tabToNextCell) — tabToNextCell resumes the
            // "continue editing across cells" flow and auto-opens an editor
            // on caseGtin; we only want to move focus there, not edit it.
            setTimeout(
              () => params.api.setFocusedCell(params.node.rowIndex, 'caseGtin'),
              0
            );
            return true;
          }
          return false;
        },
      },

      // ── Case GTIN ────────────────────────────────────────────────────────
      {
        field: 'caseGtin',
        headerComponent: HeaderGrid,
        headerComponentParams: {
          label: 'Case GTIN',
          required: true,
          tooltip:
            'GTIN on the CASE, CANNOT be the same as the Global Trade Item Number for the Unit or Innerpack',
        },
        width: 140,
        // Enabled when noCodeCase is NOT checked OR when the row is a Display Shipper
        editable: (params) => !isNoCodeCase(params) || isDisplayShipper(params),
        singleClickEdit: true,
        cellEditor: GtinCellEditor,
        cellStyle: (params) => {
          if (isDisplayShipper(params)) {
            return createCellErrorStyle('caseGtin')(params);
          }
          return createCellErrorStyle('caseGtin', caseGtinCellStyle)(params);
        },
        cellRenderer: ErrorCellRenderer,
      },

      {
        field: 'caseSellable',
        headerComponent: HeaderGrid,
        headerComponentParams: {
          label: 'Is Case Sellable?',
          tooltip:
            'Select Yes only when the case is intended for sale to the end consumer at a retail store and has its own retail-scannable barcode.',
        },
        width: 200,
        editable: (params) =>
          !isDisplayShipper(params) && !isFoodService(params),
        cellStyle: extendDisabledCellStyle(isFoodService),
        singleClickEdit: true,
        cellRenderer: YesNoCellRenderer,
        cellEditor: YesNoCellEditor,
        cellEditorPopup: true,
      },
      {
        field: 'caseInnerPackCount',
        cellDataType: 'number',
        headerComponent: HeaderGrid,
        headerComponentParams: {
          label: 'Case Innerpack Count',
          tooltip:
            'Number of INNERPACKS (caddies, trays, etc) inside the CASE (Master Case)',
        },
        width: 180,
        editable: (params) => !isDisplayShipper(params) && !isVariableWeight(params),
        singleClickEdit: true,
        cellEditor: NumberCellEditor,
        cellStyle: extendDisabledCellStyle(isVariableWeight),
      },
      {
        field: 'casePackSize',
        cellDataType: 'number',
        headerComponent: HeaderGrid,
        headerComponentParams: {
          label: 'Case Pack Size',
          tooltip: 'Total Number of Units in a CASE (Master Case)',
        },
        width: 160,
        editable: (params) => !isDisplayShipper(params),
        singleClickEdit: true,
        cellEditor: NumberCellEditor,
        cellStyle: disabledCellStyle,
      },
    ],
    [
      onRecaptureRow,
      showDisplayShippers,
      showMultipacks,
      memberId,
      showMerchandisedInTray,
      showPerishable,
      showFoodServiceBulkItems,
      showVariableWeight,
      onOpenHierarchy,
    ]
  );

  return { columnDefs };
};

export default useOnlineCoreGridColumns;
